import React, { Suspense } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import Text from 'shared/components/Text/Text';
import Button from 'shared/components/Button/Button';
import { useBreakpoints } from 'hooks/useWindowSize';
import { useOnboardingStep } from 'hooks/useOnboardingStep';
import { formatPhoneNumber } from 'shared/utils/formatPhoneNumber';
import { PHONE_NUMBERS } from 'constants/phoneNumbers';
import { PhoneIcon } from 'shared/components/icons';
import {
  BusinessHours,
  isWithinBusinessHours,
  TimeRange,
} from 'shared/utils/isWithinBusinessHours';
import { toast } from 'shared/components/Toast/Toast';
import { useTheme } from 'styled-components';
import { OnboardingStep } from 'modules/types/util';
import {
  ContentItem,
  ContentList,
  DividerWrapper,
  HorizontalLine,
  OnboardingTPVContent,
  TPVFailedContainer,
  TPVSupportHours,
} from './OnboardingTPV.styles';

const CallSupportGraphic = React.lazy(() => import('shared/components/icons/CallSupportGraphic'));

const weekdaySupportHours: TimeRange = {
  start: [9, 0],
  end: [23, 0],
};

const supportHours: BusinessHours = {
  0: null,
  1: weekdaySupportHours,
  2: weekdaySupportHours,
  3: weekdaySupportHours,
  4: weekdaySupportHours,
  5: weekdaySupportHours,
  6: { start: [11, 0], end: [20, 0] },
};

export function TPVFailed() {
  const { t } = useTranslation('translation', { keyPrefix: 'onboarding.tpv' });
  const theme = useTheme();
  const screenSize = useBreakpoints();

  const { goToStep } = useOnboardingStep();

  const handleClickCall = () => {
    if (isWithinBusinessHours(supportHours)) {
      window.location.href = `tel:${PHONE_NUMBERS.CUSTOMER_SUPPORT}`;
    } else {
      toast({
        type: 'error',
        title: t('toast.error', { keyPrefix: '' }),
        message: t('supportUnavailable'),
        theme,
      });
    }
  };

  const bodyList: string[] = t('failedBody', {
    returnObjects: true,
  }) as string[];

  return (
    <TPVFailedContainer>
      <Suspense fallback={<LoadingSpinner />}>
        <CallSupportGraphic />
      </Suspense>
      <Text as="h1" $textAlign="center" $fontWeight={700} $margin="40px 0 8px">
        {t('contactSupport')}
      </Text>
      <OnboardingTPVContent>
        <ContentList>
          {Array.isArray(bodyList) &&
            bodyList.map((item) => (
              <ContentItem key={item}>
                <Trans>{item}</Trans>
              </ContentItem>
            ))}
        </ContentList>
        <DividerWrapper>
          <HorizontalLine />
          <Text as="h2" $textAlign="center" $fontWeight={500} $margin="26px 11px 24px">
            {t('callUs')}
          </Text>
          <HorizontalLine />
        </DividerWrapper>
      </OnboardingTPVContent>
      <Button
        styleVariant="secondary"
        Icon={PhoneIcon}
        label={formatPhoneNumber(PHONE_NUMBERS.CUSTOMER_SUPPORT)}
        onClick={handleClickCall}
        isFullWidth={screenSize === 'sm'}
      />
      <TPVSupportHours>
        <Text as="p">{t('supportHoursWeekdays')}</Text>
        <Text as="p">{t('supportHoursWeekend')}</Text>
      </TPVSupportHours>
      <Button
        styleVariant="primary"
        label={t('next')}
        onClick={() => goToStep(OnboardingStep.AUTOPAY)}
        isFullWidth={screenSize === 'sm'}
      />
    </TPVFailedContainer>
  );
}

export default TPVFailed;
